import {
  transformTrackRequestData,
  transformTrackRequestDataMonolith,
} from './utils/request-transformations';

type ApiClientProps = {
  apiKey: string;
  baseUrl?: string;
  /** Local monolith uses a different path and JSON shape than legacy API Gateway. */
  useMonolith: boolean;
  config: {
    workspaceId: string;
    tenantId?: string;
  };
};

export type EventProperties = Record<string, any>;

type User = {
  email: string;
};

const sdkConfig = {
  defaultHeaders: {
    'Content-Type': 'application/json',
  },
};

const TRACK_LEGACY = 'prod/track';

class ApiClient {
  private apiKey: string;
  private baseUrl?: string;
  private workspaceId: string = '';
  private user?: User;
  private readonly useMonolith: boolean;

  constructor({ apiKey, baseUrl, config, useMonolith }: ApiClientProps) {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
    this.workspaceId = config.workspaceId;
    this.useMonolith = useMonolith;
  }

  private trackPath(): string {
    return this.useMonolith
      ? `workspaces/${this.workspaceId}/activity-logs/track`
      : TRACK_LEGACY;
  }

  fetchWithConfig = (
    endpoint: string,
    options: RequestInit = { method: 'GET' }
  ) => {
    const url = `${this.getBaseUrl()}/${endpoint}`;
    const headers = {
      ...sdkConfig.defaultHeaders,
      'x-api-key': this.getApiKey(),
      ...(options.headers || {}),
    };
    return fetch(url, { ...options, headers });
  };

  getApiKey() {
    return this.apiKey;
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  getWorkspaceId() {
    return this.workspaceId;
  }

  getUser() {
    return this.user;
  }

  identify(user: User) {
    this.user = user;
  }

  track(eventName: string, eventProperties?: EventProperties, userEmail?: string) {
    const email = userEmail || this.user?.email;
    if (!email) {
      throw new Error('User email must be provided either via identify() or as a parameter to track()');
    }
    const payload = {
      eventName,
      eventProperties,
      userProperties: { email },
    };
    const body = this.useMonolith
      ? transformTrackRequestDataMonolith(payload)
      : transformTrackRequestData(payload);
    this.fetchWithConfig(this.trackPath(), { method: 'POST', body });
  }
}

export default ApiClient;
